/*
 * MAUKAU 2026-09-26 — behaviour of the header search field (snippets/header-search-field.liquid).
 * Loaded by sections/header.liquid only when a search field setting is on.
 *
 * 1. Mobile: the full-width search row collapses when the visitor scrolls down and comes back as soon
 *    as they scroll up (CDO pattern). While collapsed, the vendor search icon reappears in the header
 *    (`.header__search-icon--collapsed-only`). The header is sticky, so the section takes the row's
 *    space as margin-bottom while collapsed — otherwise the whole page would jump by that height.
 *
 * 2. Typing animations. Search drawer: the same list is typed into the drawer input's placeholder each
 *    time the drawer opens empty. Header field: on the first page of a visit (sessionStorage), the field's text types
 *    "<prefix> <word>" for each word of the `search_typing_words` setting, once through the list,
 *    then returns to the normal field text. Skipped for prefers-reduced-motion, and stopped the moment
 *    the field is clicked.
 */
(() => {
  const header = document.querySelector('store-header');
  if (!header) return;

  /* ---------------------------------------------------------------- 1. mobile row collapse */
  const row = header.querySelector('.header__search-row');
  const section = header.closest('.shopify-section');

  if (row && section) {
    const mobile = window.matchMedia('(max-width: 699px)');
    const wrapper = header.querySelector('.header__wrapper');
    let lastY = window.scrollY;
    let ticking = false;
    let collapsed = false;

    const setCollapsed = (value) => {
      if (value === collapsed) return;

      if (value) {
        // Measured at the moment of collapsing, while the row is still open.
        const gap = parseFloat(getComputedStyle(wrapper).rowGap) || 0;
        section.style.setProperty('--header-search-row-space', `${row.offsetHeight + gap}px`);
      }

      collapsed = value;
      header.toggleAttribute('search-collapsed', value);
    };

    const update = () => {
      const y = window.scrollY;

      if (!mobile.matches || y <= row.offsetTop + row.offsetHeight) {
        setCollapsed(false);
      } else if (y > lastY + 4) {
        setCollapsed(true);
      } else if (y < lastY - 4) {
        setCollapsed(false);
      }

      lastY = y;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });

    mobile.addEventListener('change', update);
  }

  /* ---------------------------------------------------------------- 2. typing animations */
  const typingSource = document.querySelector('.header-search-field[data-typing-words]');
  const typingWords = typingSource ? typingSource.dataset.typingWords.split(',').map((word) => word.trim()).filter(Boolean) : [];
  const typingLead = typingSource && typingSource.dataset.typingPrefix ? `${typingSource.dataset.typingPrefix.trim()} ` : '';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /*
   * Search drawer input: same list, typed into the input's placeholder each time the drawer opens
   * empty. Stops as soon as the visitor types, and when the drawer closes.
   */
  const drawer = document.getElementById('search-drawer');
  const drawerInput = drawer && drawer.querySelector('input[type="search"]');

  if (drawerInput && typingWords.length > 0 && !reducedMotion) {
    const basePlaceholder = drawerInput.placeholder;
    let drawerTimer = null;

    const stopDrawer = () => {
      clearTimeout(drawerTimer);
      drawerTimer = null;
      drawerInput.placeholder = basePlaceholder;
    };

    const playDrawer = () => {
      let index = 0;
      let chars = 0;
      let erasing = false;

      const tick = () => {
        if (drawerInput.value || !drawer.hasAttribute('open')) {
          stopDrawer();
          return;
        }

        const word = typingWords[index];
        chars += erasing ? -1 : 1;
        drawerInput.placeholder = typingLead + word.slice(0, chars);

        if (!erasing && chars >= word.length) {
          erasing = true;
          drawerTimer = setTimeout(tick, 1200);
        } else if (erasing && chars <= 0) {
          erasing = false;
          index++;
          if (index >= typingWords.length) {
            stopDrawer();
            return;
          }
          drawerTimer = setTimeout(tick, 250);
        } else {
          drawerTimer = setTimeout(tick, erasing ? 35 : 70);
        }
      };

      stopDrawer();
      drawerInput.placeholder = typingLead;
      drawerTimer = setTimeout(tick, 500);
    };

    new MutationObserver(() => {
      if (drawer.hasAttribute('open')) {
        if (!drawerInput.value && !drawerTimer) playDrawer();
      } else {
        stopDrawer();
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });

    drawerInput.addEventListener('input', stopDrawer);
  }

  /* Header field: once per visit, on page load. */
  const fields = [...document.querySelectorAll('.header-search-field[data-typing-words]')];
  if (fields.length === 0 || reducedMotion) return;

  let alreadyPlayed = false;
  try {
    alreadyPlayed = sessionStorage.getItem('pdv-search-typing') === '1';
    sessionStorage.setItem('pdv-search-typing', '1');
  } catch (error) {
    // Storage unavailable (private mode, blocked): play it, it is harmless.
  }
  if (alreadyPlayed) return;

  const words = typingWords;
  if (words.length === 0) return;

  const lead = typingLead;
  const spans = fields.map((field) => field.querySelector('.header-search-field__placeholder'));
  const baseText = spans[0].textContent;

  let wordIndex = 0;
  let charIndex = 0;
  let deleting = false;
  let timer = null;

  const show = (text) => spans.forEach((span) => { span.textContent = text; });

  const stop = () => {
    clearTimeout(timer);
    fields.forEach((field) => field.classList.remove('is-typing'));
    show(baseText);
  };

  const step = () => {
    const word = words[wordIndex];

    if (!deleting) {
      charIndex++;
      show(lead + word.slice(0, charIndex));

      if (charIndex >= word.length) {
        deleting = true;
        timer = setTimeout(step, 1200);
        return;
      }

      timer = setTimeout(step, 70);
      return;
    }

    charIndex--;
    show(lead + word.slice(0, charIndex));

    if (charIndex > 0) {
      timer = setTimeout(step, 35);
      return;
    }

    deleting = false;
    wordIndex++;

    if (wordIndex >= words.length) {
      stop();
      return;
    }

    timer = setTimeout(step, 250);
  };

  fields.forEach((field) => field.addEventListener('click', stop, { once: true }));

  timer = setTimeout(() => {
    fields.forEach((field) => field.classList.add('is-typing'));
    show(lead);
    timer = setTimeout(step, 400);
  }, 900);
})();
